import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PromoAd300x250, PromoAd728x90, PromoAd300x100 } from './PromoAds';

// =============================================================================
// AD CONFIGURATION
// Set USE_PROMO_ADS to true to show VelvetStream self-promotional ads
// Set to false to show JuicyAds
// =============================================================================
const USE_PROMO_ADS = false; // Toggle: true = VelvetStream promos, false = JuicyAds

// =============================================================================
// JUICYADS CONFIGURATION
// Get your zone IDs from: https://www.juicyads.com/
// Publisher Dashboard -> Get Ad Code -> Select Zone
// =============================================================================
const JUICYADS_ZONES = {
  // Home Page Banners - matched to JuicyAds dashboard sizes
  leaderboard_top: '1115336',           // 728x90 Leaderboard
  leaderboard_bottom: '1115337',        // 728x90 Leaderboard
  in_feed_1: '1115333',                 // 300x250 Image
  in_feed_2: '1115334',                 // 300x250 Image
  in_feed_3: '1115335',                 // 300x250 Image
  sticky_footer: '1115336',             // 728x90 Leaderboard (reuse)
  
  // Video Page Banners
  video_sidebar: '1115344',             // 300x250 Image
  in_video: '1115338',                  // 300x250 in-Video Ads
  video_float: '1115340',               // 300x250 Layer/Float Ads
  
  // Mobile Banners
  mobile_home: '1115339',               // 300x100 Mobile Ads
  mobile_video: '1115341',              // 300x100 Mobile Ads
  mobile_categories: '1115343',         // 300x100 Mobile Ads
};

// JuicyAds Ad Component - ID must be exactly the zone number
const JuicyAdRaw = ({ zoneId, width, height }) => {
  const containerRef = useRef(null);
  const injected = useRef(false);

  useEffect(() => {
    if (injected.current || !containerRef.current || !zoneId) return;
    injected.current = true;

    const container = containerRef.current;
    container.innerHTML = '';
    
    // Create ins element with ID exactly matching the zone number (JuicyAds requirement)
    const ins = document.createElement('ins');
    ins.id = String(zoneId);  // ID must be exactly the zone number
    ins.setAttribute('data-width', width);
    ins.setAttribute('data-height', height);
    container.appendChild(ins);
    
    // Push to JuicyAds queue
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.setAttribute('data-cfasync', 'false');
    script.textContent = `(adsbyjuicy = window.adsbyjuicy || []).push({'adzone':${zoneId}});`;
    container.appendChild(script);
  }, [zoneId, width, height]);

  if (!zoneId) {
    return (
      <div 
        className="bg-[#0A0A0A] rounded-lg flex items-center justify-center border border-dashed border-white/10"
        style={{ width, height, minWidth: width, minHeight: height }}
      >
        <div className="text-center p-4">
          <p className="text-gray-500 text-xs font-medium">JuicyAds</p>
          <p className="text-gray-600 text-[10px]">{width}x{height}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      style={{ minWidth: width, minHeight: height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    />
  );
};

// ==================== HOME PAGE ADS ====================

// Leaderboard Top (728x90)
export const LeaderboardAd = () => {
  if (USE_PROMO_ADS) return <div className="w-full max-w-[728px] mx-auto flex justify-center"><PromoAd728x90 /></div>;
  return (
    <div className="w-full max-w-[728px] min-h-[90px] relative bg-[#0A0A0A] rounded-lg overflow-hidden mx-auto flex items-center justify-center" data-testid="leaderboard-ad">
      <JuicyAdRaw zoneId={JUICYADS_ZONES.leaderboard_top} width={728} height={90} />
    </div>
  );
};

// Leaderboard Bottom (728x90)
export const LeaderboardAdBottom = () => {
  if (USE_PROMO_ADS) return <div className="w-full max-w-[728px] mx-auto flex justify-center"><PromoAd728x90 /></div>;
  return (
    <div className="w-full max-w-[728px] min-h-[90px] relative bg-[#0A0A0A] rounded-lg overflow-hidden mx-auto flex items-center justify-center" data-testid="leaderboard-ad-bottom">
      <JuicyAdRaw zoneId={JUICYADS_ZONES.leaderboard_bottom} width={728} height={90} />
    </div>
  );
};

// In-Feed Ad - Rotates through zones 1, 2, 3
export const InFeedAd = ({ index = 0 }) => {
  // Use index to determine which zone to use for better distribution
  const zones = [JUICYADS_ZONES.in_feed_1, JUICYADS_ZONES.in_feed_2, JUICYADS_ZONES.in_feed_3];
  const zoneId = zones[index % zones.length];
  
  if (USE_PROMO_ADS) return <div className="w-full h-full flex items-center justify-center"><PromoAd300x250 /></div>;
  return (
    <div className="bg-[#0A0A0A] rounded-md overflow-hidden flex items-center justify-center min-h-[250px] w-full" data-testid="in-feed-ad">
      <JuicyAdRaw zoneId={zoneId} width={300} height={250} />
    </div>
  );
};

// In-Feed Ad 2 (300x250)
export const InFeedAd2 = () => {
  if (USE_PROMO_ADS) return <div className="w-full h-full flex items-center justify-center"><PromoAd300x250 /></div>;
  return (
    <div className="bg-[#0A0A0A] rounded-md overflow-hidden flex items-center justify-center min-h-[250px] w-full" data-testid="in-feed-ad-2">
      <JuicyAdRaw zoneId={JUICYADS_ZONES.in_feed_2} width={300} height={250} />
    </div>
  );
};

// In-Feed Ad 3 (300x250)
export const InFeedAd3 = () => {
  if (USE_PROMO_ADS) return <div className="w-full h-full flex items-center justify-center"><PromoAd300x250 /></div>;
  return (
    <div className="bg-[#0A0A0A] rounded-md overflow-hidden flex items-center justify-center min-h-[250px] w-full" data-testid="in-feed-ad-3">
      <JuicyAdRaw zoneId={JUICYADS_ZONES.in_feed_3} width={300} height={250} />
    </div>
  );
};

// Sidebar Ad (300x250) - Uses video_sidebar zone
export const SidebarAd = ({ onClose }) => {
  if (USE_PROMO_ADS) return <PromoAd300x250 />;
  return (
    <div className="w-[300px] min-h-[250px] relative bg-[#0A0A0A] rounded-lg overflow-hidden flex items-center justify-center" data-testid="sidebar-ad">
      {onClose && (
        <button onClick={onClose} className="absolute top-2 right-2 p-1 bg-black/50 rounded-full z-10">
          <X className="w-3 h-3 text-gray-400" />
        </button>
      )}
      <JuicyAdRaw zoneId={JUICYADS_ZONES.video_sidebar} width={300} height={250} />
    </div>
  );
};

// Sticky Footer Ad (728x90)
export const StickyFooterAd = ({ onClose }) => {
  const [isVisible, setIsVisible] = useState(true);
  if (!isVisible) return null;
  
  if (USE_PROMO_ADS) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#0A0A0A]/95 backdrop-blur border-t border-white/10 py-2 px-4" data-testid="sticky-footer-ad">
        <div className="max-w-[728px] mx-auto relative flex items-center justify-center">
          <button onClick={() => { setIsVisible(false); onClose?.(); }} className="absolute -top-2 right-0 p-1 bg-black/80 hover:bg-white/10 rounded-full z-10">
            <X className="w-4 h-4 text-gray-500" />
          </button>
          <PromoAd728x90 />
        </div>
      </div>
    );
  }
  
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#0A0A0A]/95 backdrop-blur border-t border-white/10 py-2 px-4" data-testid="sticky-footer-ad">
      <div className="max-w-[728px] mx-auto relative flex items-center justify-center">
        <button onClick={() => { setIsVisible(false); onClose?.(); }} className="absolute -top-2 right-0 p-1 bg-black/80 hover:bg-white/10 rounded-full z-10">
          <X className="w-4 h-4 text-gray-500" />
        </button>
        <JuicyAdRaw zoneId={JUICYADS_ZONES.sticky_footer} width={728} height={90} />
      </div>
    </div>
  );
};

// Mobile Ad - Home (300x100)
export const MobileAdHome = () => {
  if (USE_PROMO_ADS) return <div className="block md:hidden mx-auto flex justify-center"><PromoAd300x100 /></div>;
  return (
    <div className="block md:hidden bg-[#0A0A0A] rounded-md overflow-hidden flex items-center justify-center min-h-[100px] mx-auto" data-testid="mobile-ad-home">
      <JuicyAdRaw zoneId={JUICYADS_ZONES.mobile_home} width={300} height={100} />
    </div>
  );
};

// ==================== VIDEO PAGE ADS ====================

// Video Sidebar Ad (300x250)
export const VideoPageAd = () => {
  if (USE_PROMO_ADS) return <PromoAd300x250 />;
  return (
    <div className="bg-[#0A0A0A] rounded-lg overflow-hidden flex items-center justify-center min-h-[250px] w-full" data-testid="video-page-ad">
      <JuicyAdRaw zoneId={JUICYADS_ZONES.video_sidebar} width={300} height={250} />
    </div>
  );
};

// In-Video Ad (300x250)
export const InVideoAd = () => {
  if (USE_PROMO_ADS) return <PromoAd300x250 />;
  return (
    <div className="bg-[#0A0A0A] rounded-lg overflow-hidden flex items-center justify-center min-h-[250px] w-full" data-testid="in-video-ad">
      <JuicyAdRaw zoneId={JUICYADS_ZONES.in_video} width={300} height={250} />
    </div>
  );
};

// Video Float Ad (300x250)
export const VideoFloatAd = ({ onClose }) => {
  const [isVisible, setIsVisible] = useState(true);
  if (!isVisible) return null;
  
  if (USE_PROMO_ADS) {
    return (
      <div className="fixed bottom-4 right-4 z-40 rounded-lg overflow-hidden shadow-2xl" data-testid="video-float-ad">
        <button onClick={() => { setIsVisible(false); onClose?.(); }} className="absolute -top-2 -right-2 p-1 bg-black/80 hover:bg-white/10 rounded-full z-10">
          <X className="w-4 h-4 text-gray-500" />
        </button>
        <PromoAd300x250 />
      </div>
    );
  }
  
  return (
    <div className="fixed bottom-4 right-4 z-40 bg-[#0A0A0A] rounded-lg overflow-hidden shadow-2xl border border-white/10" data-testid="video-float-ad">
      <button onClick={() => { setIsVisible(false); onClose?.(); }} className="absolute -top-2 -right-2 p-1 bg-black/80 hover:bg-white/10 rounded-full z-10">
        <X className="w-4 h-4 text-gray-500" />
      </button>
      <JuicyAdRaw zoneId={JUICYADS_ZONES.video_float} width={300} height={250} />
    </div>
  );
};

// Mobile Ad - Video (300x100)
export const MobileAdVideo = () => {
  if (USE_PROMO_ADS) return <div className="block md:hidden mx-auto flex justify-center"><PromoAd300x100 /></div>;
  return (
    <div className="block md:hidden bg-[#0A0A0A] rounded-md overflow-hidden flex items-center justify-center min-h-[100px] mx-auto" data-testid="mobile-ad-video">
      <JuicyAdRaw zoneId={JUICYADS_ZONES.mobile_video} width={300} height={100} />
    </div>
  );
};

// Mobile Ad - Categories (300x100)
export const MobileAdCategories = () => {
  if (USE_PROMO_ADS) return <div className="block md:hidden mx-auto flex justify-center"><PromoAd300x100 /></div>;
  return (
    <div className="block md:hidden bg-[#0A0A0A] rounded-md overflow-hidden flex items-center justify-center min-h-[100px] mx-auto" data-testid="mobile-ad-categories">
      <JuicyAdRaw zoneId={JUICYADS_ZONES.mobile_categories} width={300} height={100} />
    </div>
  );
};

// ==================== NON-AD COMPONENTS ====================

export const PrerollAd = ({ onSkip, skipAfter = 5 }) => {
  const [countdown, setCountdown] = useState(skipAfter);
  const [canSkip, setCanSkip] = useState(false);
  
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanSkip(true);
    }
  }, [countdown]);
  
  return (
    <div className="absolute inset-0 bg-black z-20 flex items-center justify-center" data-testid="preroll-ad">
      <div className="text-center">
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center mb-4 mx-auto">
          <span className="text-3xl font-bold text-white">AD</span>
        </div>
        <p className="text-white text-lg mb-2">Advertisement</p>
        {canSkip ? (
          <button onClick={onSkip} className="px-6 py-2 bg-white text-black font-medium rounded hover:bg-gray-200 transition-colors">Skip Ad</button>
        ) : (
          <p className="text-gray-500 text-sm">Skip in {countdown}s</p>
        )}
      </div>
    </div>
  );
};

export const InterstitialAd = ({ onClose }) => {
  const navigate = useNavigate();
  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" data-testid="interstitial-ad">
      <div className="relative max-w-lg w-full bg-[#121212] border border-white/10 rounded-lg overflow-hidden">
        <button onClick={onClose} className="absolute top-3 right-3 p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors z-10">
          <X className="w-5 h-5 text-white" />
        </button>
        <div className="aspect-video bg-gradient-to-br from-purple-900 to-pink-900 flex items-center justify-center relative">
          <div className="text-center p-6">
            <h2 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Upgrade to Premium</h2>
            <p className="text-white/80 text-lg">Remove all ads & unlock exclusive features</p>
          </div>
        </div>
        <div className="p-6 text-center">
          <p className="text-gray-400 mb-4">Starting at just $9.99/month</p>
          <div className="flex gap-3 justify-center">
            <button onClick={onClose} className="px-6 py-2 border border-white/20 text-gray-400 rounded hover:bg-white/5">Maybe Later</button>
            <button onClick={() => { onClose(); navigate('/subscription'); }} className="px-6 py-2 bg-white text-black font-medium rounded hover:bg-gray-200">Go Premium</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdBanner = ({ type = 'sidebar', onClose }) => {
  switch (type) {
    case 'leaderboard':
    case 'header':
      return <LeaderboardAd />;
    case 'sidebar':
      return <SidebarAd onClose={onClose} />;
    case 'feed':
      return <InFeedAd />;
    default:
      return <SidebarAd onClose={onClose} />;
  }
};

export default AdBanner;
